// Security Utilities for IncidentForm

// XSS Prevention - Sanitize user input
export const sanitizeInput = (input) => {
  if (!input) return '';
  return String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Validate phone number
export const isValidPhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{6,14}$/;
  return phoneRegex.test(phone);
};

// Check file type by magic numbers (more secure than extension)
export const validateFileType = async (file, allowedTypes) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      const arr = new Uint8Array(e.target.result).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16).padStart(2, '0');
      }
      
      const signatures = {
        'image/jpeg': ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2', 'ffd8ffe3', 'ffd8ffe8'],
        'image/png': ['89504e47'],
        'image/gif': ['47494638'],
        'application/pdf': ['25504446'],
        'application/zip': ['504b0304', '504b0506', '504b0708']
      };
      
      const isValid = allowedTypes.some(type => 
        signatures[type] && signatures[type].some(sig => header.startsWith(sig))
      );
      
      resolve(isValid);
    };
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};

// Generate secure random filename
export const generateSecureFileName = (originalName) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split('.').pop().toLowerCase();
  const safeExtension = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'zip'].includes(extension) ? extension : 'bin';
  return `upload_${timestamp}_${random}.${safeExtension}`;
};

// Rate limiting for OTP requests
export class RateLimiter {
  constructor(maxRequests = 3, windowMs = 300000) { // 3 requests per 5 minutes
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }
  
  canMakeRequest() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    return this.requests.length < this.maxRequests;
  }
  
  recordRequest() {
    this.requests.push(Date.now());
  }
  
  getRemainingTime() {
    if (this.requests.length === 0) return 0;
    const oldestRequest = Math.min(...this.requests);
    const timeElapsed = Date.now() - oldestRequest;
    return Math.max(0, this.windowMs - timeElapsed);
  }
}

// Sanitize filename to prevent path traversal
export const sanitizeFileName = (filename) => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/\.{2,}/g, '.')
    .substring(0, 255);
};

// Validate text length and content
export const validateText = (text, minLength = 1, maxLength = 1000) => {
  if (!text || typeof text !== 'string') return false;
  const sanitized = sanitizeInput(text);
  return sanitized.length >= minLength && sanitized.length <= maxLength;
};

// Generic error messages (hide technical details)
export const getGenericErrorMessage = (error) => {
  const errorMessages = {
    network: 'Unable to connect to the server. Please check your internet connection and try again.',
    validation: 'Please check your inputs and try again.',
    upload: 'File upload failed. Please ensure your files meet the requirements and try again.',
    auth: 'Verification failed. Please check your credentials and try again.',
    timeout: 'Request timed out. Please try again.',
    server: 'We\'re experiencing technical difficulties. Please try again later.',
    rate_limit: 'Too many requests. Please wait a few minutes before trying again.',
  };
  
  // Never expose actual error details
  if (error?.message?.toLowerCase().includes('network') || error?.message?.toLowerCase().includes('fetch')) {
    return errorMessages.network;
  }
  if (error?.response?.status === 429) {
    return errorMessages.rate_limit;
  }
  if (error?.response?.status === 413) {
    return 'File size too large. Please ensure files are under 25 MB.';
  }
  if (error?.response?.status >= 500) {
    return errorMessages.server;
  }
  if (error?.response?.status === 408) {
    return errorMessages.timeout;
  }
  
  return errorMessages.server; // Default safe message
};

// Content Security Policy meta tag injection
export const injectCSPMeta = () => {
  if (document.querySelector('meta[http-equiv="Content-Security-Policy"]')) return;
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = "default-src 'self'; script-src 'self' https://www.google.com https://www.gstatic.com 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline' https://cdnjs.cloudflare.com https://fonts.googleapis.com; font-src 'self' https://cdnjs.cloudflare.com https://fonts.gstatic.com; img-src 'self' data: blob: https://flagcdn.com; frame-src https://www.google.com; connect-src 'self' https://www.google.com;";
  document.head.prepend(meta);
};

// Prevent clickjacking
export const preventClickjacking = () => {
  if (window.self !== window.top) {
    document.body.innerHTML = '';
    window.top.location = window.self.location;
  }
};

// Disable console in production
export const secureConsole = () => {
  if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    console.warn = () => {};
  }
};

// Detect and block devtools in production
export const initSecurityMeasures = () => {
  preventClickjacking();
  secureConsole();
  
  // Prevent right-click context menu in production
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
  }
};
